import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authFetch, parseJsonResponse } from '../lib/api';
import {
  ACTIVE_EXPLOITANT_EVENT,
  getActiveExploitantId,
  setActiveExploitantId,
} from '../lib/activeExploitant';

const ActiveExploitantContext = createContext(null);

export function ActiveExploitantProvider({ user, children }) {
  const isRoot = user?.role === 'root';
  const [exploitants, setExploitants] = useState([]);
  const [loading, setLoading] = useState(isRoot);
  const [activeId, setActiveId] = useState(() => (isRoot ? getActiveExploitantId() : null));

  const loadExploitants = useCallback(async () => {
    if (!isRoot) return;
    setLoading(true);
    try {
      const res = await authFetch('/api/exploitants');
      const data = await parseJsonResponse(res);
      if (data.success) {
        setExploitants(data.data || []);
      }
    } catch (err) {
      console.error('[ActiveExploitantProvider]', err);
    } finally {
      setLoading(false);
    }
  }, [isRoot]);

  useEffect(() => {
    loadExploitants();
  }, [loadExploitants]);

  useEffect(() => {
    if (!isRoot) return undefined;

    const syncFromStorage = () => {
      setActiveId(getActiveExploitantId());
    };

    window.addEventListener(ACTIVE_EXPLOITANT_EVENT, syncFromStorage);
    return () => window.removeEventListener(ACTIVE_EXPLOITANT_EVENT, syncFromStorage);
  }, [isRoot]);

  const selectExploitant = useCallback((id) => {
    const next = id ? String(id) : null;
    setActiveExploitantId(next);
    setActiveId(next);
  }, []);

  const activeExploitant = useMemo(
    () => exploitants.find((e) => String(e.id) === String(activeId)) || null,
    [exploitants, activeId],
  );

  const value = useMemo(
    () => ({
      isRoot,
      exploitants,
      loading,
      activeId,
      activeExploitant,
      selectExploitant,
      reloadExploitants: loadExploitants,
    }),
    [isRoot, exploitants, loading, activeId, activeExploitant, selectExploitant, loadExploitants],
  );

  return (
    <ActiveExploitantContext.Provider value={value}>{children}</ActiveExploitantContext.Provider>
  );
}

export function useActiveExploitant() {
  const ctx = useContext(ActiveExploitantContext);
  if (!ctx) {
    return {
      isRoot: false,
      exploitants: [],
      loading: false,
      activeId: null,
      activeExploitant: null,
      selectExploitant: () => {},
      reloadExploitants: () => {},
    };
  }
  return ctx;
}
